import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Store, Search, Filter, CheckCircle, XCircle, AlertCircle, 
  Eye, Trash2, DollarSign, Package, Clock, Ban, MoreVertical,
  ChevronLeft, ChevronRight, ExternalLink, FileText, FileCheck, FileX
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMerchantsPage() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [stats, setStats] = useState({ pending: 0, active: 0, suspended: 0 });
  const [filters, setFilters] = useState({ status: '', business_type: '', search: '' });
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [newCommission, setNewCommission] = useState('');
  
  // Document review state
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState(null);
  const [documentsPending, setDocumentsPending] = useState(0);

  useEffect(() => {
    loadMerchants();
    loadDocumentsPending();
  }, [filters]);

  const getAuthHeaders = () => {
    const authHeader = getAuthHeader();
    return {
      'Content-Type': 'application/json',
      ...authHeader
    };
  };

  const loadMerchants = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.business_type) params.append('business_type', filters.business_type);
      if (filters.search) params.append('search', filters.search);

      const res = await fetch(`${API_URL}/api/admin/merchants?${params}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      
      if (data.success) {
        setMerchants(data.merchants);
        setStats(data.stats);
      }
    } catch (error) {
      toast.error('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };
  
  const loadDocumentsPending = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/merchants/stats/overview`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success && data.stats) {
        setDocumentsPending(data.stats.documents_pending || 0);
      }
    } catch (error) {
      console.error('Failed to load documents pending count');
    }
  };
  
  const openDocumentReview = (merchant) => {
    setSelectedMerchant(merchant);
    setSelectedDocs(merchant.verification_documents);
    setActionReason('');
    setShowDocModal(true);
  };
  
  const handleDocumentVerify = async (status) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/merchants/${selectedMerchant.id}/documents/verify`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          documents_status: status,
          rejection_reason: status === 'rejected' ? actionReason : null 
        })
      });
      
      if (res.ok) {
        toast.success(status === 'approved' ? 'Documents approved. Merchant is now active!' : 'Documents rejected');
        setShowDocModal(false);
        loadMerchants();
        loadDocumentsPending();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to verify documents');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openActionModal = (merchant, action) => {
    setSelectedMerchant(merchant);
    setModalAction(action);
    setActionReason('');
    setNewCommission(merchant.commission_rate?.toString() || '10');
    setShowModal(true);
  };

  const handleStatusUpdate = async (status) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/merchants/${selectedMerchant.id}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status, reason: actionReason })
      });
      
      if (res.ok) {
        toast.success(`Merchant ${status === 'active' ? 'approved' : status}`);
        setShowModal(false);
        loadMerchants();
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCommissionUpdate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/merchants/${selectedMerchant.id}/commission`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ commission_rate: parseFloat(newCommission) })
      });
      
      if (res.ok) {
        toast.success('Commission rate updated');
        setShowModal(false);
        loadMerchants();
      } else {
        throw new Error('Failed to update commission');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure? This will delete the merchant and all their products.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/merchants/${selectedMerchant.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Merchant deleted');
        setShowModal(false);
        loadMerchants();
      }
    } catch (error) {
      toast.error('Failed to delete merchant');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      active: 'bg-green-500/20 text-green-400',
      suspended: 'bg-red-500/20 text-red-400',
      rejected: 'bg-slate-500/20 text-slate-400'
    };
    return styles[status] || styles.pending;
  };

  const getTypeBadge = (type) => {
    const styles = {
      affiliate: 'bg-blue-500/20 text-blue-400',
      dropshipper: 'bg-purple-500/20 text-purple-400',
      direct: 'bg-orange-500/20 text-orange-400'
    };
    return styles[type] || styles.affiliate;
  };

  return (
    <div className="p-6 pt-20" data-testid="admin-merchants-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Merchant Management</h1>
          <p className="text-slate-400 text-sm mt-1">Manage marketplace merchants and their products</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div 
          className={`bg-slate-800 border rounded-xl p-4 cursor-pointer transition-colors ${
            filters.status === 'pending' ? 'border-yellow-500' : 'border-slate-700 hover:border-slate-600'
          }`}
          onClick={() => setFilters(f => ({ ...f, status: f.status === 'pending' ? '' : 'pending' }))}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-400/50" />
          </div>
        </div>
        <div 
          className={`bg-slate-800 border rounded-xl p-4 cursor-pointer transition-colors ${
            filters.status === 'active' ? 'border-green-500' : 'border-slate-700 hover:border-slate-600'
          }`}
          onClick={() => setFilters(f => ({ ...f, status: f.status === 'active' ? '' : 'active' }))}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Active Merchants</p>
              <p className="text-2xl font-bold text-green-400">{stats.active}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400/50" />
          </div>
        </div>
        <div 
          className={`bg-slate-800 border rounded-xl p-4 cursor-pointer transition-colors ${
            filters.status === 'suspended' ? 'border-red-500' : 'border-slate-700 hover:border-slate-600'
          }`}
          onClick={() => setFilters(f => ({ ...f, status: f.status === 'suspended' ? '' : 'suspended' }))}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Suspended</p>
              <p className="text-2xl font-bold text-red-400">{stats.suspended}</p>
            </div>
            <Ban className="w-8 h-8 text-red-400/50" />
          </div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Merchants</p>
              <p className="text-2xl font-bold text-white">{stats.pending + stats.active + stats.suspended}</p>
            </div>
            <Users className="w-8 h-8 text-slate-400/50" />
          </div>
        </div>
      </div>
      
      {/* Documents Pending Banner */}
      {documentsPending > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6" data-testid="documents-pending-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-amber-400" />
              <div>
                <p className="font-medium text-amber-400">Document Verification Required</p>
                <p className="text-sm text-slate-300" data-testid="documents-pending-count">{documentsPending} merchant(s) awaiting document review</p>
              </div>
            </div>
            <button
              onClick={() => setFilters(f => ({ ...f, status: 'pending' }))}
              className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm font-medium"
            >
              Review Now
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search merchants..."
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
          <select
            value={filters.business_type}
            onChange={(e) => setFilters(f => ({ ...f, business_type: e.target.value }))}
            className="px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="">All Types</option>
            <option value="affiliate">Affiliate</option>
            <option value="dropshipper">Dropshipper</option>
            <option value="direct">Direct Seller</option>
          </select>
          {(filters.status || filters.business_type || filters.search) && (
            <button
              onClick={() => setFilters({ status: '', business_type: '', search: '' })}
              className="px-4 py-2 text-slate-400 hover:text-white text-sm"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Merchants Table */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : merchants.length === 0 ? (
          <div className="text-center py-12">
            <Store className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No merchants found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-400 font-medium text-sm">Merchant</th>
                  <th className="text-left px-4 py-3 text-slate-400 font-medium text-sm">Type</th>
                  <th className="text-left px-4 py-3 text-slate-400 font-medium text-sm">Status</th>
                  <th className="text-left px-4 py-3 text-slate-400 font-medium text-sm">Documents</th>
                  <th className="text-left px-4 py-3 text-slate-400 font-medium text-sm">Products</th>
                  <th className="text-left px-4 py-3 text-slate-400 font-medium text-sm">Commission</th>
                  <th className="text-left px-4 py-3 text-slate-400 font-medium text-sm">Joined</th>
                  <th className="text-right px-4 py-3 text-slate-400 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {merchants.map((merchant) => (
                  <tr key={merchant.id} className="border-t border-slate-700/50 hover:bg-slate-700/30">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                          {merchant.logo_url ? (
                            <img src={merchant.logo_url} alt="" className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <Store className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-white">{merchant.business_name}</p>
                          <p className="text-xs text-slate-400">{merchant.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded capitalize ${getTypeBadge(merchant.business_type)}`}>
                        {merchant.business_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded capitalize ${getStatusBadge(merchant.status)}`}>
                        {merchant.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {merchant.verification_documents ? (
                        <button
                          data-testid={`merchant-docs-badge-${merchant.id}`}
                          onClick={() => openDocumentReview(merchant)}
                          className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                            merchant.documents_status === 'pending' 
                              ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' 
                              : merchant.documents_status === 'approved'
                                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                : merchant.documents_status === 'rejected'
                                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                  : 'bg-slate-600/20 text-slate-400 hover:bg-slate-500/30'
                          }`}
                        >
                          {merchant.documents_status === 'pending' && <Clock className="w-3 h-3" />}
                          {merchant.documents_status === 'approved' && <FileCheck className="w-3 h-3" />}
                          {merchant.documents_status === 'rejected' && <FileX className="w-3 h-3" />}
                          <span className="capitalize">{merchant.documents_status || 'Review'}</span>
                        </button>
                      ) : (
                        <span className="text-xs text-slate-500">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white">{merchant.total_products || 0}</td>
                    <td className="px-4 py-3 text-white">{merchant.commission_rate || 10}%</td>
                    <td className="px-4 py-3 text-slate-400 text-sm">
                      {new Date(merchant.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <a
                          href={`/store/${merchant.store_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-slate-600 rounded-lg text-slate-400 hover:text-white"
                          title="View Store"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        {merchant.status === 'pending' && (
                          <>
                            <button
                              onClick={() => openActionModal(merchant, 'approve')}
                              className="p-2 hover:bg-green-500/20 rounded-lg text-slate-400 hover:text-green-400"
                              title="Approve"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openActionModal(merchant, 'reject')}
                              className="p-2 hover:bg-red-500/20 rounded-lg text-slate-400 hover:text-red-400"
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {merchant.status === 'active' && (
                          <button
                            onClick={() => openActionModal(merchant, 'suspend')}
                            className="p-2 hover:bg-yellow-500/20 rounded-lg text-slate-400 hover:text-yellow-400"
                            title="Suspend"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                        {merchant.status === 'suspended' && (
                          <button
                            onClick={() => openActionModal(merchant, 'reactivate')}
                            className="p-2 hover:bg-green-500/20 rounded-lg text-slate-400 hover:text-green-400"
                            title="Reactivate"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openActionModal(merchant, 'commission')}
                          className="p-2 hover:bg-blue-500/20 rounded-lg text-slate-400 hover:text-blue-400"
                          title="Edit Commission"
                        >
                          <DollarSign className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setSelectedMerchant(merchant); handleDelete(); }}
                          className="p-2 hover:bg-red-500/20 rounded-lg text-slate-400 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {showModal && selectedMerchant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              {modalAction === 'approve' && 'Approve Merchant'}
              {modalAction === 'reject' && 'Reject Merchant'}
              {modalAction === 'suspend' && 'Suspend Merchant'}
              {modalAction === 'reactivate' && 'Reactivate Merchant'}
              {modalAction === 'commission' && 'Update Commission Rate'}
            </h3>
            
            <div className="mb-4 p-3 bg-slate-900 rounded-lg">
              <p className="font-medium text-white">{selectedMerchant.business_name}</p>
              <p className="text-sm text-slate-400">{selectedMerchant.email}</p>
            </div>

            {modalAction === 'commission' ? (
              <div className="mb-4">
                <label className="block text-sm text-slate-300 mb-2">Commission Rate (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={newCommission}
                  onChange={(e) => setNewCommission(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                />
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm text-slate-300 mb-2">Reason (optional)</label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none"
                  placeholder="Enter reason for this action..."
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
              >
                Cancel
              </button>
              {modalAction === 'approve' && (
                <button
                  onClick={() => handleStatusUpdate('active')}
                  className="flex-1 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg"
                >
                  Approve
                </button>
              )}
              {modalAction === 'reject' && (
                <button
                  onClick={() => handleStatusUpdate('rejected')}
                  className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg"
                >
                  Reject
                </button>
              )}
              {modalAction === 'suspend' && (
                <button
                  onClick={() => handleStatusUpdate('suspended')}
                  className="flex-1 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg"
                >
                  Suspend
                </button>
              )}
              {modalAction === 'reactivate' && (
                <button
                  onClick={() => handleStatusUpdate('active')}
                  className="flex-1 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg"
                >
                  Reactivate
                </button>
              )}
              {modalAction === 'commission' && (
                <button
                  onClick={handleCommissionUpdate}
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg"
                >
                  Update
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Document Review Modal */}
      {showDocModal && selectedMerchant && selectedDocs && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" data-testid="doc-review-modal">
          <div className="w-full max-w-2xl bg-slate-800 border border-slate-700 rounded-xl p-6 my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Document Verification</h3>
              <button 
                onClick={() => setShowDocModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            {/* Merchant Info */}
            <div className="mb-6 p-4 bg-slate-900 rounded-lg">
              <div className="flex items-center gap-3">
                <Store className="w-10 h-10 text-slate-400" />
                <div>
                  <p className="font-medium text-white">{selectedMerchant.business_name}</p>
                  <p className="text-sm text-slate-400">{selectedMerchant.email}</p>
                  <span className={`text-xs px-2 py-0.5 rounded capitalize ${getTypeBadge(selectedMerchant.business_type)}`}>
                    {selectedMerchant.business_type}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Document List */}
            <div className="space-y-4 mb-6">
              <h4 className="text-sm font-medium text-slate-300">Uploaded Documents</h4>
              {Object.entries(selectedDocs).map(([docType, docInfo]) => {
                // Function to view document securely
                const viewDocument = async () => {
                  try {
                    const response = await fetch(docInfo.url, {
                      headers: getAuthHeaders()
                    });
                    if (!response.ok) {
                      toast.error('Failed to load document');
                      return;
                    }
                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    window.open(blobUrl, '_blank');
                  } catch (error) {
                    toast.error('Failed to load document');
                  }
                };
                
                return (
                  <div key={docType} data-testid={`doc-item-${docType}`} className="flex items-center justify-between p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm font-medium text-white capitalize">{docType.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-slate-400">{docInfo.filename}</p>
                      </div>
                    </div>
                    <button
                      onClick={viewDocument}
                      className="px-3 py-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded text-sm flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                  </div>
                );
              })}
            </div>
            
            {/* Current Status */}
            {selectedMerchant.documents_status === 'rejected' && selectedMerchant.documents_rejection_reason && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">
                  <strong>Previously Rejected:</strong> {selectedMerchant.documents_rejection_reason}
                </p>
              </div>
            )}
            
            {/* Rejection Reason Input */}
            <div className="mb-6">
              <label className="block text-sm text-slate-300 mb-2">Rejection Reason (required if rejecting)</label>
              <textarea
                data-testid="doc-review-reason"
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none"
                placeholder="Enter reason for rejection (e.g., ID is blurry, address doesn't match)..."
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                data-testid="doc-review-cancel"
                onClick={() => setShowDocModal(false)}
                className="flex-1 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                data-testid="doc-review-reject"
                onClick={() => {
                  if (!actionReason.trim()) {
                    toast.error('Please provide a rejection reason');
                    return;
                  }
                  handleDocumentVerify('rejected');
                }}
                className="flex-1 py-3 bg-red-600/20 border border-red-500/30 hover:bg-red-600/30 text-red-400 rounded-lg flex items-center justify-center gap-2"
              >
                <FileX className="w-5 h-5" />
                Reject Documents
              </button>
              <button
                data-testid="doc-review-approve"
                onClick={() => handleDocumentVerify('approved')}
                className="flex-1 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center justify-center gap-2"
              >
                <FileCheck className="w-5 h-5" />
                Approve & Activate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
